import dayjs from "dayjs";
import styled from "styled-components";

import React, { useState, useEffect, Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Button,
  Container,
  Feed,
  Grid,
  Header,
  Icon,
  Image,
  Loader,
  Segment,
  Table,
  Divider,
} from "semantic-ui-react";

import { UserEdit } from "./edit";
import { ErrorWrap } from "../../../helpers/error";
import { BackButton } from "../../common/back-button";
import { RoleRequired } from "../../auth/role-required";
import { Pagination } from "../../../components/common/pagination";
import { sliceList } from "../../../helpers/filter";

import { t } from "../../../constant/translations";
import { fetchUsers } from "../../../redux/modules/user";
import { fetchAuditLog } from "../../../redux/modules/audit-log";
import { logEntryTitle, MAP_PARAMS_TO_VALUE_LABEL_PAIRS } from "./helpers";
import { getGravatarSrc } from "../../../components/common/avatar";
import { permissions } from "../../../constant/permissions";

const MAP_ACTION_TO_ICON = {
  update: "cut",
  create: "plus",
  destroy: "trash",
  reconcile: "euro",
};

const MAP_ACTION_TO_COLOR = {
  update: "yellow",
  create: "green",
  destroy: "red",
  reconcile: "blue",
};

const FeedSummary = styled(Feed.Summary)`
  & > a {
    vertical-align: baseline;
  }
`;

const StyledFirstName = styled.span`
  color: ${(props) => props.theme.primary};
`;

const TeamDetailsContainer = () => {
  const [expanded, setExpanded] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const params = useParams();
  const userId = parseInt(params.userId);

  const users = useSelector((state) => state.user.entities);
  const log = useSelector((state) => state.auditLog.entities);
  const error = useSelector((state) =>
    ErrorWrap.fromError(state.user.error || state.auditLog.error)
  );
  const loading = useSelector(
    (state) => state.user.pending || state.auditLog.pending
  );
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchUsers(userId));
    dispatch(fetchAuditLog(userId));
  }, [userId]);

  const handleToggleExpanded = (id) =>
    setExpanded({
      ...expanded,
      [id]: !expanded[id],
    });

  const renderParamsTable = (logEntry) => {
    const responseValueLabelPairs = Object.entries(
      MAP_PARAMS_TO_VALUE_LABEL_PAIRS
    )
      .filter(([p]) => logEntry.response[p])
      .map(([p, f]) => f(logEntry.response[p]));

    let body;
    body = (
      <Table.Row>
        {responseValueLabelPairs.map(({ label, value }) => (
          <Table.Cell key={label} collapsing={true}>
            {value}
          </Table.Cell>
        ))}
      </Table.Row>
    );

    return (
      <Table celled striped fixed singleLine>
        <Table.Header>
          <Table.Row>
            {responseValueLabelPairs.map(({ label }) => (
              <Table.HeaderCell key={label}>{label}</Table.HeaderCell>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>{body}</Table.Body>
      </Table>
    );
  };

  if (error.exists()) {
    return <div>{error.getGlobal()}</div>;
  }

  if (!users || !log) {
    return <Loader active />;
  }

  if (loading) {
    return <Loader active />;
  }

  const page = sliceList(log, currentPage - 1, pageSize);

  const user = users.find((u) => u.id === userId);

  const renderFeeds = page.map((rec) => {
    return (
      <Feed.Event key={rec.id}>
        <Feed.Label>
          <Icon
            name={MAP_ACTION_TO_ICON[rec.action]}
            color={MAP_ACTION_TO_COLOR[rec.action]}
          />
        </Feed.Label>
        <Feed.Content>
          <Feed.Date>
            {dayjs(rec.created_at).format("YYYY-MM-DD HH:mm")}
          </Feed.Date>
          <FeedSummary>
            <StyledFirstName>{user.first_name}</StyledFirstName>{" "}
            {logEntryTitle(rec)}
            <Button
              basic
              floated={"right"}
              size={"tiny"}
              onClick={(_) => handleToggleExpanded(rec.id)}
            >
              {t("management.more")}{" "}
              <Icon name={`caret ${expanded[rec.id] ? "up" : "down"}`} />
            </Button>
          </FeedSummary>
          {expanded[rec.id] && renderParamsTable(rec)}
        </Feed.Content>
      </Feed.Event>
    );
  });

  return (
    <Grid padded>
      <Grid.Row>
        <Grid.Column widescreen={3} computer={6} tablet={16}>
          <Grid padded>
            <Grid.Row>
              <Grid.Column>
                <BackButton />
                <RoleRequired admin={true} permission={permissions.teamEdit}>
                  <UserEdit entity={user} />
                </RoleRequired>
                <Segment.Group>
                  <Segment>
                    <Image
                      size="medium"
                      rounded={true}
                      src={
                        user.profile_picture
                          ? user.profile_picture.url
                          : getGravatarSrc(user.email, null, 300)
                      }
                    />
                    <Header
                      as="h4"
                      content={`${user.first_name} ${user.last_name}`}
                      subheader={`${user.email} (${t(`roles.${user.role}`)})`}
                    />
                  </Segment>
                </Segment.Group>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
        <Grid.Column widescreen={13} computer={10} tablet={16}>
          <Grid padded>
            <Grid.Row>
              <Grid.Column>
                {loading ? (
                  <Loader active />
                ) : (
                  <Container>
                    <Header as="h2">{t("management.activity_report")}</Header>
                    <Pagination
                      current={currentPage}
                      total={log.length}
                      onChange={(newPage, newPageSize) => {
                        setCurrentPage(newPage);
                        setPageSize(newPageSize);
                      }}
                      pageSize={pageSize}
                    />
                    <Feed>{renderFeeds}</Feed>
                  </Container>
                )}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export const TeamMemberDetails = TeamDetailsContainer;
